import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";

interface Permission {
  id: number;
  userId: number;
  resource: string;
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export function usePermissions() {
  const { user } = useAuth();

  const { data: permissions = [], isLoading } = useQuery<Permission[]>({
    queryKey: ["/api/user-permissions"],
    enabled: !!user && (user.role === 'user' || user.role === 'employee'), // Pre zamestnancov a employee
  });

  // Admin, superadmin a owner majú vždy všetky oprávnenia
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'owner';

  // Default oprávnenia pre nových používateľov (ak nemajú explicitné oprávnenia)
  const defaultResources = ['dashboard', 'clients', 'tasks', 'invoices', 'price_quotes', 'dobropisy', 'profile'];
  
  // Helper funkcie pre kontrolu oprávnení
  const hasPermission = (resource: string, action: 'view' | 'create' | 'edit' | 'delete'): boolean => {
    // Admin, superadmin a owner majú vždy všetky oprávnenia
    if (isAdmin) {
      return true;
    }

    // Pre zamestnancov kontrolujeme granulárne oprávnenia
    const permission = permissions.find(p => p.resource === resource);
    
    // Ak má explicitné oprávnenie, použijeme ho
    if (permission) {
      switch (action) {
        case 'view': return permission.canView;
        case 'create': return permission.canCreate;
        case 'edit': return permission.canEdit;
        case 'delete': return permission.canDelete;
        default: return false;
      }
    }
    
    // Ak nemá explicitné oprávnenie, použijeme default pre základné zdroje
    // Toto umožňuje novým používateľom vidieť základné stránky
    if (defaultResources.includes(resource) && action === 'view') {
      return true;
    }
    
    return false;
  };

  const canViewClients = () => hasPermission('clients', 'view');
  const canViewTasks = () => hasPermission('tasks', 'view');
  const canViewInvoices = () => hasPermission('invoices', 'view');
  const canViewPriceQuotes = () => hasPermission('price_quotes', 'view');
  const canViewDobropisy = () => hasPermission('dobropisy', 'view');
  const canViewUsers = () => hasPermission('users', 'view');
  const canViewSystemSettings = () => hasPermission('system_settings', 'view');

  return {
    permissions,
    isLoading,
    hasPermission,
    canViewClients,
    canViewTasks,
    canViewInvoices,
    canViewPriceQuotes,
    canViewDobropisy,
    canViewUsers,
    canViewSystemSettings
  };
}